import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Source, Layer, useMap } from 'react-map-gl/mapbox'

// Shared bezier helpers — must stay in sync with MapConnectionLayer/MapFlowParticles
function bezierPoint(start, end, t) {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len < 0.001) return start
  const mx = (start[0] + end[0]) / 2
  const my = (start[1] + end[1]) / 2
  const offset = Math.min(len * 0.18, 2.5)
  const cx = mx - (dy / len) * offset
  const cy = my + (dx / len) * offset
  return [
    (1 - t) * (1 - t) * start[0] + 2 * (1 - t) * t * cx + t * t * end[0],
    (1 - t) * (1 - t) * start[1] + 2 * (1 - t) * t * cy + t * t * end[1],
  ]
}

// Tangent of the quadratic bezier at t (for correct truck rotation)
function bezierTangent(start, end, t) {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len < 0.001) return [dx, dy]
  const mx = (start[0] + end[0]) / 2
  const my = (start[1] + end[1]) / 2
  const offset = Math.min(len * 0.18, 2.5)
  const cx = mx - (dy / len) * offset
  const cy = my + (dx / len) * offset
  const tx = 2 * (1 - t) * (cx - start[0]) + 2 * t * (end[0] - cx)
  const ty = 2 * (1 - t) * (cy - start[1]) + 2 * t * (end[1] - cy)
  return [tx, ty]
}

// Draw a realistic delivery truck icon on canvas
function createTruckIcon(size = 36) {
  const canvas = document.createElement('canvas')
  const dpr = 2
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2

  // Truck dimensions
  const truckW = size * 0.82
  const truckH = size * 0.5
  const tx = cx - truckW / 2
  const ty = cy - truckH / 2 - 1

  // --- Drop shadow ---
  ctx.shadowColor = 'rgba(0,0,0,0.3)'
  ctx.shadowBlur = 4
  ctx.shadowOffsetY = 2

  // --- Cargo container (back) ---
  const cargoW = truckW * 0.6
  const cargoH = truckH * 0.85
  const cargoX = tx
  const cargoY = ty + truckH - cargoH
  const cr = 2.5

  ctx.beginPath()
  ctx.moveTo(cargoX + cr, cargoY)
  ctx.lineTo(cargoX + cargoW - cr, cargoY)
  ctx.quadraticCurveTo(cargoX + cargoW, cargoY, cargoX + cargoW, cargoY + cr)
  ctx.lineTo(cargoX + cargoW, cargoY + cargoH - cr)
  ctx.quadraticCurveTo(cargoX + cargoW, cargoY + cargoH, cargoX + cargoW - cr, cargoY + cargoH)
  ctx.lineTo(cargoX + cr, cargoY + cargoH)
  ctx.quadraticCurveTo(cargoX, cargoY + cargoH, cargoX, cargoY + cargoH - cr)
  ctx.lineTo(cargoX, cargoY + cr)
  ctx.quadraticCurveTo(cargoX, cargoY, cargoX + cr, cargoY)
  ctx.closePath()
  ctx.fillStyle = '#f8fafc'
  ctx.fill()
  ctx.shadowColor = 'transparent'

  // Cargo container border
  ctx.strokeStyle = '#cbd5e1'
  ctx.lineWidth = 0.8
  ctx.stroke()

  // Cargo horizontal lines (panels)
  const panelY1 = cargoY + cargoH * 0.35
  const panelY2 = cargoY + cargoH * 0.65
  ctx.beginPath()
  ctx.moveTo(cargoX + 2, panelY1)
  ctx.lineTo(cargoX + cargoW - 2, panelY1)
  ctx.moveTo(cargoX + 2, panelY2)
  ctx.lineTo(cargoX + cargoW - 2, panelY2)
  ctx.strokeStyle = '#e2e8f0'
  ctx.lineWidth = 0.6
  ctx.stroke()

  // Small blue stripe on the cargo (branding)
  ctx.fillStyle = '#6366f1'
  ctx.fillRect(cargoX, cargoY + cargoH * 0.08, cargoW, cargoH * 0.08)

  // --- Cab (front) ---
  const cabW = truckW * 0.35
  const cabH = truckH * 0.7
  const cabX = cargoX + cargoW + 1
  const cabY = ty + truckH - cabH

  ctx.shadowColor = 'rgba(0,0,0,0.2)'
  ctx.shadowBlur = 3
  ctx.shadowOffsetY = 1

  ctx.beginPath()
  ctx.moveTo(cabX, cabY + cr)
  ctx.quadraticCurveTo(cabX, cabY, cabX + cr, cabY)
  ctx.lineTo(cabX + cabW - cr * 2, cabY)
  ctx.quadraticCurveTo(cabX + cabW, cabY + cr, cabX + cabW, cabY + cr * 2)
  ctx.lineTo(cabX + cabW, cabY + cabH - cr)
  ctx.quadraticCurveTo(cabX + cabW, cabY + cabH, cabX + cabW - cr, cabY + cabH)
  ctx.lineTo(cabX, cabY + cabH)
  ctx.closePath()
  ctx.fillStyle = '#3b82f6'
  ctx.fill()
  ctx.shadowColor = 'transparent'

  // Cab outline
  ctx.strokeStyle = '#2563eb'
  ctx.lineWidth = 0.6
  ctx.stroke()

  // Windshield
  const wsX = cabX + cabW * 0.15
  const wsY = cabY + cabH * 0.12
  const wsW = cabW * 0.65
  const wsH = cabH * 0.4
  ctx.beginPath()
  ctx.moveTo(wsX + 1.5, wsY)
  ctx.lineTo(wsX + wsW - 1.5, wsY)
  ctx.quadraticCurveTo(wsX + wsW, wsY, wsX + wsW, wsY + 1.5)
  ctx.lineTo(wsX + wsW, wsY + wsH - 1.5)
  ctx.quadraticCurveTo(wsX + wsW, wsY + wsH, wsX + wsW - 1.5, wsY + wsH)
  ctx.lineTo(wsX + 1.5, wsY + wsH)
  ctx.quadraticCurveTo(wsX, wsY + wsH, wsX, wsY + wsH - 1.5)
  ctx.lineTo(wsX, wsY + 1.5)
  ctx.quadraticCurveTo(wsX, wsY, wsX + 1.5, wsY)
  ctx.closePath()
  ctx.fillStyle = 'rgba(186, 230, 253, 0.8)'
  ctx.fill()
  ctx.strokeStyle = '#1d4ed8'
  ctx.lineWidth = 0.4
  ctx.stroke()

  // Windshield reflection
  ctx.beginPath()
  ctx.moveTo(wsX + 1, wsY + 1)
  ctx.lineTo(wsX + wsW * 0.3, wsY + 1)
  ctx.lineTo(wsX + 1, wsY + wsH * 0.5)
  ctx.closePath()
  ctx.fillStyle = 'rgba(255,255,255,0.45)'
  ctx.fill()

  // --- Wheels ---
  const wheelR = truckH * 0.15
  const wheelY = ty + truckH + wheelR * 0.3

  // Rear wheel
  const rearWheelX = cargoX + cargoW * 0.25
  ctx.beginPath()
  ctx.arc(rearWheelX, wheelY, wheelR, 0, Math.PI * 2)
  ctx.fillStyle = '#334155'
  ctx.fill()
  ctx.beginPath()
  ctx.arc(rearWheelX, wheelY, wheelR * 0.45, 0, Math.PI * 2)
  ctx.fillStyle = '#94a3b8'
  ctx.fill()

  // Front wheel
  const frontWheelX = cabX + cabW * 0.5
  ctx.beginPath()
  ctx.arc(frontWheelX, wheelY, wheelR, 0, Math.PI * 2)
  ctx.fillStyle = '#334155'
  ctx.fill()
  ctx.beginPath()
  ctx.arc(frontWheelX, wheelY, wheelR * 0.45, 0, Math.PI * 2)
  ctx.fillStyle = '#94a3b8'
  ctx.fill()

  // --- Undercarriage line ---
  ctx.beginPath()
  ctx.moveTo(rearWheelX + wheelR + 1, ty + truckH)
  ctx.lineTo(frontWheelX - wheelR - 1, ty + truckH)
  ctx.strokeStyle = '#475569'
  ctx.lineWidth = 1
  ctx.stroke()

  return { canvas, width: size * dpr, height: size * dpr }
}

function MapSimulationLayer({ nodes, edges, themeMode }) {
  const [time, setTime] = useState(0)
  const animationRef = useRef(null)
  const iconLoaded = useRef(false)
  const { current: mapInstance } = useMap()

  // Load the truck icon into the map
  useEffect(() => {
    if (!mapInstance || iconLoaded.current) return
    const map = mapInstance.getMap()
    if (!map) return
    if (map.hasImage('truck-icon')) {
      iconLoaded.current = true
      return
    }

    const { canvas, width, height } = createTruckIcon(36)
    const imageData = canvas.getContext('2d').getImageData(0, 0, width, height)
    map.addImage('truck-icon', imageData, { pixelRatio: 2 })
    iconLoaded.current = true
  }, [mapInstance])

  useEffect(() => {
    let lastTimestamp = performance.now()

    const animate = timestamp => {
      const delta = (timestamp - lastTimestamp) / 1000
      lastTimestamp = timestamp
      setTime(t => (t + delta * 0.08) % 1)
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  const geojson = useMemo(() => {
    const features = []
    const PARTICLES_PER_EDGE = 3

    edges.forEach(edge => {
      const source = nodes.find(n => n.id === edge.source)
      const target = nodes.find(n => n.id === edge.target)
      if (!source?.data.lat || !target?.data.lat) return

      const sourcePos = [source.data.lng, source.data.lat]
      const targetPos = [target.data.lng, target.data.lat]

      for (let i = 0; i < PARTICLES_PER_EDGE; i++) {
        const offset = i / PARTICLES_PER_EDGE
        const t = (time + offset) % 1
        const position = bezierPoint(sourcePos, targetPos, t)

        // Smoother fade: full opacity in middle, fading at ends
        const alpha = Math.sin(t * Math.PI)
        const opacity = 0.3 + alpha * 0.7

        // Compute bearing from the bezier tangent at t (truck faces direction of travel)
        const [tx, ty] = bezierTangent(sourcePos, targetPos, t)
        const bearing = (Math.atan2(tx, ty) * 180) / Math.PI

        features.push({
          type: 'Feature',
          properties: {
            opacity,
            size: 0.7 + alpha * 0.3,
            bearing: bearing - 90, // Truck drawn sideways, rotate to face forward
          },
          geometry: {
            type: 'Point',
            coordinates: position,
          },
        })
      }
    })

    return { type: 'FeatureCollection', features }
  }, [nodes, edges, time])

  if (!iconLoaded.current) return null

  return (
    <Source id="sim-particles" type="geojson" data={geojson}>
      {/* Truck icons */}
      <Layer
        id="sim-particles-icons"
        type="symbol"
        layout={{
          'icon-image': 'truck-icon',
          'icon-size': ['get', 'size'],
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        }}
        paint={{
          'icon-opacity': ['get', 'opacity'],
        }}
      />
      {/* Subtle trail glow beneath each truck */}
      <Layer
        id="sim-particles-glow"
        type="circle"
        beforeId="sim-particles-icons"
        paint={{
          'circle-radius': 8,
          'circle-color': themeMode === 'dark' ? '#818cf8' : '#6366f1',
          'circle-opacity': ['*', ['get', 'opacity'], 0.12],
          'circle-blur': 1.2,
        }}
      />
    </Source>
  )
}

export default React.memo(MapSimulationLayer)
